import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Box } from '@doltech/ui/lib/figma/Common/Box';
import DragOut, { DragOutSpot } from '@doltech/ui/lib/figma/QuestionWithSpots/DragOut';
import { QuestionTypeEnum } from '@doltech/utils/lib/constants';
import { motion } from 'framer-motion';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import * as React from 'react';
import { useDragLayer, XYCoord } from 'react-dnd';
import styled from 'styled-components';

import { DoTestDragOutInstance } from '../student-do-test/model/Question/do-test-drag-out.store';

interface DragOutProps extends React.HTMLAttributes<HTMLDivElement> {
  data: DoTestDragOutInstance;
}

export const DragOutAnswer = observer((props: DragOutProps) => {
  const dragConstraints = React.useRef(null);
  const { data, ...rest } = props;
  const { no, image, spots, userAnswers, questions, answer, removeAnswerByIndex } = data;
  const floatingBoxRef = React.useRef(null);
  const [containerHeight, setContainerHeight] = React.useState(undefined);

  React.useEffect(() => {
    setTimeout(() => {
      setContainerHeight(floatingBoxRef.current?.clientHeight);
    }, 100);
  }, [containerHeight, floatingBoxRef]);

  return (
    <motion.div ref={dragConstraints}>
      <Container className="dol-question" height={containerHeight} {...rest}>
        <DragOut
          id={`question-no-${no}`}
          answers={getSnapshot(userAnswers)}
          no={no}
          ref={floatingBoxRef}
          image={image}
          questions={questions}
          spots={spots}
          onAnswer={answer}
          onRemoveAnswer={removeAnswerByIndex}
        />
      </Container>
      <DragPreview />
    </motion.div>
  );
});

const Container = styled<any>(Box)<{ height }>`
  min-height: ${(p) => p.height}px;
  height: max-content;
`;

export const dragLayerStyle: React.CSSProperties = {
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 100,
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
};

export function getItemStyles(initialOffset: XYCoord | null, currentOffset: XYCoord | null) {
  if (!initialOffset || !currentOffset) {
    return {
      display: 'none',
    };
  }

  const { x, y } = currentOffset;

  const transform = `translate(${x}px, ${y}px)`;
  return {
    transform,
    WebkitTransform: transform,
  };
}

const DragPreview = () => {
  const { itemType, isDragging, item, initialOffset, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (itemType === QuestionTypeEnum.DRAG_OUT && isDragging && !isNil(item)) {
    return (
      <div style={dragLayerStyle} className={`drag-out-drag-preview-${item.spot.text}`}>
        <div style={getItemStyles(initialOffset, currentOffset)}>
          <DragOutPreviewSpot text={item.spot.text} />
        </div>
      </div>
    );
  }

  return null;
};

const DragOutPreviewSpot = styled(DragOutSpot)`
  .spot-item-wrapper {
    background-color: ${colorsV2.blue40};
    border: 1px solid ${colorsV2.blue40};
  }
`;
